import { useCallback } from "react";
import type { MessageData, MessageEventHandlers } from "@/types/chat.types";

/**
 * Custom hook for message operations
 *
 * This hook provides a clean interface for message-related operations
 * that can be used across different components.
 *
 * @param onEditMessage - Callback when a message is edited
 * @param onDeleteMessage - Callback when a message is deleted
 * @param onReplyToMessage - Callback when replying to a message
 * @returns Event handlers object
 */
export function useMessageOperations(
  onEditMessage: (messageId: string, newText: string) => void,
  onDeleteMessage: (messageId: string, agentUsername?: string) => void,
  onReplyToMessage: (message: MessageData) => void
): MessageEventHandlers {
  const handleEdit = useCallback(
    (messageId: string, newText: string) => {
      if (!newText.trim()) {
        console.warn("Cannot edit message: text is empty");
        return;
      }
      onEditMessage(messageId, newText);
    },
    [onEditMessage]
  );

  const handleDelete = useCallback(
    (messageId: string, agentUsername?: string) => {
      onDeleteMessage(messageId, agentUsername);
    },
    [onDeleteMessage]
  );

  const handleReply = useCallback(
    (message: MessageData) => {
      onReplyToMessage(message);
    },
    [onReplyToMessage]
  );

  return {
    onEdit: handleEdit,
    onDelete: handleDelete,
    onReply: handleReply,
  };
}
